import React, { useContext } from "react";
import { useSelector } from "react-redux";
import { makePeerConsumerSelector } from "../../../helpers/Selectors";
import RoomContext from "../../../context/RoomContext";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import MicOnIcon from "@material-ui/icons/MicNoneOutlined";
import MicOffIcon from "@material-ui/icons/MicOffOutlined";
import CamOnIcon from "@material-ui/icons/VideocamOutlined";
import CamOffIcon from "@material-ui/icons/VideocamOffOutlined";
import RaiseHandIcon from "@material-ui/icons/PanToolOutlined";
import ExitIcon from "@material-ui/icons/ExitToAppOutlined";
import NotInterested from "@material-ui/icons/NotInterestedOutlined";
import UnpinPeerIcon from "@material-ui/icons/Star";
import PinPeerIcon from "@material-ui/icons/StarBorder";
import Participant from "../Participant";
import GuardianAdmin from "../../Management/Messaging/icons/GuardianAdmin";
import GuardianAdminDelete from "../../Management/Messaging/icons/GuardianAdminDelete";

const PeerListItem = ({ id, isModerator, children, isSelected, onClick }) => {
  const intl = useIntl();
  const { roomClient } = useContext(RoomContext);
  const peer = useSelector((state) => state.peers[id]);
  const getPeerConsumers = makePeerConsumerSelector();
  const mode = useSelector((state) => state.room.mode);
  const selectedPeerId = useSelector((state) => state.room.selectedPeerId);
  const { micConsumer, screenConsumer, webcamConsumer } = useSelector((state) =>
    getPeerConsumers(state, id)
  );
  const webinar = useSelector((state) => state.room.webinar);
  const hasWebinarPresenterPermission = useSelector((state) =>
    state.me.roles.some((role) =>
      state.room.permissionsFromRoles.MODERATE_WEBINAR.includes(role)
    )
  );

  const micEnabled =
    Boolean(micConsumer) &&
    !micConsumer.locallyPaused &&
    !micConsumer.remotelyPaused;

  const screenVisible =
    Boolean(screenConsumer) &&
    !screenConsumer.locallyPaused &&
    !screenConsumer.remotelyPaused;

  const camEnabled =
    Boolean(webcamConsumer) &&
    !webcamConsumer.locallyPaused &&
    !webcamConsumer.remotelyPaused;

  const indicators = [];
  const actions = [];
  /*  if (screenConsumer) {
    actions.push({
      label: intl.formatMessage({
        id: "tooltip.muteScreenSharing",
        defaultMessage: "Mute participant share",
      }),
      disabled: peer.peerScreenInProgress,
      onClick: () => {
        screenVisible
          ? roomClient.modifyPeerConsumer(peer.id, "screen", true)
          : roomClient.modifyPeerConsumer(peer.id, "screen", false);
      },
      children: screenVisible ? <ScreenIcon /> : <ScreenOffIcon />,
    });
  }*/

  actions.push({
    label: intl.formatMessage({
      id: micEnabled ? "tooltip.muteParticipant" : "tooltip.unmuteParticipant",
      defaultMessage: "Mute participant",
    }),
    disabled: peer.peerAudioInProgress,
    onClick: () => {
      micEnabled
        ? roomClient.modifyPeerConsumer(peer.id, "mic", true)
        : roomClient.modifyPeerConsumer(peer.id, "mic", false);
    },
    children: micEnabled ? <MicOnIcon /> : <MicOffIcon />,
  });

  actions.push({
    label: intl.formatMessage({
      id: camEnabled ? "tooltip.muteCamera" : "tooltip.unmuteCamera",
      defaultMessage: "Mute Camera",
    }),
    onClick: () => {
      camEnabled
        ? roomClient.modifyPeerConsumer(peer.id, "webcam", true)
        : roomClient.modifyPeerConsumer(peer.id, "webcam", false);
    },
    children: camEnabled ? <CamOnIcon /> : <CamOffIcon />,
  });

  if ((isModerator && !webinar) || (hasWebinarPresenterPermission && webinar)) {
    actions.push({
      label: intl.formatMessage({
        id: "tooltip.kickParticipant",
        defaultMessage: "Kick out participant",
      }),
      disabled: peer.peerKickInProgress,
      onClick: () => {
        roomClient.kickPeer(peer.id);
      },
      children: <ExitIcon />,
    });
  }

  if (
    (isModerator && !webinar) ||
    (hasWebinarPresenterPermission &&
      webinar &&
      !peer.roles.some((role) => ["moderator"].includes(role)))
  ) {
    actions.push({
      label: !peer.roles.some((role) => ["moderator"].includes(role))
        ? intl.formatMessage({
            id: "tooltip.makeModerator",
            defaultMessage: "Make Moderator",
          })
        : intl.formatMessage({
            id: "tooltip.removeModerator",
            defaultMessage: "Unsubsribe From Moderator",
          }),
      onClick: () => {
        !peer.roles.some((role) => ["moderator"].includes(role))
          ? roomClient.sendAddRole(peer.id, "moderator")
          : roomClient.sendRemoveRole(peer.id, "moderator");
      },
      children: !peer.roles.some((role) => ["moderator"].includes(role)) ? (
        <GuardianAdmin />
      ) : (
        <GuardianAdminDelete />
      ),
    });
  }

  if (mode === "filmstrip") {
    actions.push({
      label: intl.formatMessage({
        id:
          id === selectedPeerId
            ? "tooltip.unpinParticipant"
            : "tooltip.pinParticipant",
        defaultMessage: "Pin Participant",
      }),
      disabled: false,
      onClick: () => {
        roomClient.setSelectedPeer(id);
      },
      children: id === selectedPeerId ? <UnpinPeerIcon /> : <PinPeerIcon />,
    });
  }
  if (webinar) {
    if (
      peer.raiseHandState &&
      ((isModerator && !webinar) || (hasWebinarPresenterPermission && webinar))
    ) {
      indicators.push({
        label: intl.formatMessage({
          id: "peer.raisedHand",
          defaultMessage: "Raised Hand",
        }),
        onClick: () => {
          roomClient.sendAddRole(peer.id, "webinar_speaker");
        },
        style: { color: "#3289EF", borderColor: "#3289EF" },
        children: <RaiseHandIcon />,
      });
    }
    if (
      peer.roles.some((role) => ["webinar_speaker"].includes(role)) &&
      ((isModerator && !webinar) || (hasWebinarPresenterPermission && webinar))
    ) {
      indicators.push({
        label: intl.formatMessage({
          id: "peer.cancelRaisedHand",
          defaultMessage: "Cancel raised hand",
        }),
        onClick: () => {
          roomClient.sendRemoveRole(peer.id, "webinar_speaker");
        },
        children: <NotInterested />,
      });
    }
  }

  return (
    <Participant
      participant={peer}
      isSelected={isSelected}
      actions={actions}
      indicators={indicators}
      onClick={onClick}
      soundOn={micEnabled}
    >
      {children}
    </Participant>
  );
};

PeerListItem.propTypes = {
  id: PropTypes.string,
  isModerator: PropTypes.bool,
  children: PropTypes.node,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
};

export default PeerListItem;
